import { useCallback, useMemo, type ChangeEvent } from 'react'
import { Input, Label, MultiSelect, Select, Switch, Textarea } from '@quickmd-llc/design-system'
import {
  VISIT_METHOD_IN_PERSON,
  VISIT_METHOD_OFFLINE_ASYNC,
  VISIT_METHOD_VIDEO_ONLY,
  VISIT_METHOD_VIDEO_PHONE,
  getVisitMethod,
  visitMethodToFlags,
} from '../../visitMethods'

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

/**
 * Per-field validation state attached to each VisitType entry in the form.
 * These fields are not on VisitTypeAdminApiOutContract — they are orchestrator-side UI state.
 */
type VisitTypeErrors = {
  visitLengthError: string | boolean
  visitLengthLocalError: string
  coastUSDError: string | boolean
  coastUSDLocalError: string
  cancellationFeeError: string | boolean
  cancellationFeeLocalError: string
  visitNameError: string | boolean
  visitNameLocalError: string
  visitDescriptionError: string | boolean
  visitDescriptionLocalError: string
  consentFormError: string | boolean
  consentFormLocalError: string
  visibilityTypeError: string
  intakeFormError?: string | boolean
  intakeFormLocalError?: string
}

/**
 * Mutable form state for a single visit type, extended from the API contract.
 * The orchestrator (viewservice.jsx / newservice.jsx) stores this in local
 * useState and passes it down. Cost/CancellationFee are strings here because
 * the orchestrator formats them via formatMoney before storing.
 */
export type VisitTypeFormState = {
  VisitTypeId: string | null
  Name: string | null
  Description: string | null
  Duration: number
  Cost: string | number
  CancellationFee: string | number
  VisibilityType: string | null
  IsMedicationAssistedTreatment: boolean
  IsPostBookingReminderRequired: boolean
  IsPhoneVisitCommunicationTypeAllowed: boolean
  IsInPerson: boolean
  IsAsynchronous: boolean
  IsNewPatientSpacingEnabled: boolean
  ConsentForms: ConsentFormItem[] | null
  IntakeForm?: { IntakeFormId?: string | null; Name?: string | null } | null
  IntakeFormId?: string | null
  errors: VisitTypeErrors
  isNew?: boolean
}

/**
 * Intake form template item (shape from IntakeFormTemplateAdminApiOutContract).
 * extractIntakeFormsList returns unknown[] so we define the shape we actually use.
 */
type IntakeFormItem = {
  IntakeFormId?: string | null
  Name?: string | null
}

/**
 * Consent form item — used both for MultiSelect options ({ ConsentFormId, Name }) and for the
 * working consent entries stored on a visit type. The validation and save paths read the runtime
 * `id` + `isActive` fields (see checkFormsErrors + newservice/viewservice save mapping), so a
 * selected consent must carry them; they're absent on the raw API contract.
 */
type ConsentFormItem = {
  ConsentFormId?: string | null
  Name?: string | null
  id?: string | null
  isActive?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const onlyNumbersRegex = /^\d*\.?\d*$/

const VISIT_DESCRIPTION_MAX = 500

const VISIBILITY_OPTIONS = [
  { label: 'All patients', value: 'all' },
  { label: 'New patients only', value: 'new' },
  { label: 'Established patients only', value: 'established' },
  { label: 'No patients (unpublished)', value: 'none' },
  { label: 'No patients (Deep linking)', value: 'deeplink' },
] as const

function buildDurationOptions(): number[] {
  const opts: number[] = []
  for (let i = 5; i <= 60; ) {
    opts.push(i)
    i += i < 15 ? 1 : 5
  }
  return opts
}

const DURATION_OPTIONS = buildDurationOptions()

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  serviceTypeConfig: VisitTypeFormState
  serviceTypes: VisitTypeFormState[]
  setServiceTypes: (types: VisitTypeFormState[]) => void
  intakeForms: IntakeFormItem[]
  additionalConsentFormsOptions: ConsentFormItem[]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const VisitTypeForm = ({
  serviceTypeConfig,
  serviceTypes,
  setServiceTypes,
  intakeForms,
  additionalConsentFormsOptions,
}: Props) => {
  const {
    VisitTypeId: id,
    Name,
    Duration,
    Cost,
    CancellationFee,
    Description,
    VisibilityType,
    ConsentForms,
    IntakeForm,
    IntakeFormId,
    errors,
    IsMedicationAssistedTreatment,
    IsPostBookingReminderRequired,
    IsNewPatientSpacingEnabled,
  } = serviceTypeConfig

  const visitMethod = getVisitMethod(serviceTypeConfig)

  // Consent forms state — DS MultiSelect expects { value, label }[] options + string[] selected values.
  // consentOptions: purely derived from additionalConsentFormsOptions.
  // selectedConsentIds: derived from ConsentForms on the serviceTypeConfig. The orchestrator updates
  // serviceTypeConfig.ConsentForms via onConsentFormsChange → setServiceTypes, so ConsentForms always
  // reflects the current selection. On cancel, the orchestrator resets serviceTypeConfig
  // which naturally resets ConsentForms here. No local state needed.
  const consentOptions = useMemo(
    () =>
      additionalConsentFormsOptions.map((t) => ({
        value: t.ConsentFormId ?? '',
        label: t.Name ?? '',
      })),
    [additionalConsentFormsOptions],
  )

  const selectedConsentIds = useMemo(
    () =>
      (ConsentForms ?? [])
        .map((cf) => cf.id ?? cf.ConsentFormId)
        .filter((cid): cid is string => cid !== null && cid !== undefined),
    [ConsentForms],
  )

  const update = useCallback(
    (field: keyof VisitTypeFormState, value: VisitTypeFormState[typeof field]) => {
      setServiceTypes(serviceTypes.map((t) => (t.VisitTypeId === id ? { ...t, [field]: value } : t)))
    },
    [id, serviceTypes, setServiceTypes],
  )

  const updateWithErrors = useCallback(
    (
      field: keyof VisitTypeFormState,
      value: VisitTypeFormState[typeof field],
      clearErrors: Partial<VisitTypeErrors>,
    ) => {
      setServiceTypes(
        serviceTypes.map((t) =>
          t.VisitTypeId === id ? { ...t, [field]: value, errors: { ...t.errors, ...clearErrors } } : t,
        ),
      )
    },
    [id, serviceTypes, setServiceTypes],
  )

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) =>
    updateWithErrors('Name', e.target.value, { visitNameError: false, visitNameLocalError: '' })

  const onDurationChange = (e: ChangeEvent<HTMLSelectElement>) =>
    updateWithErrors('Duration', Number(e.target.value), {
      visitLengthError: false,
      visitLengthLocalError: '',
    })

  const onVisitMethodChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const flags = visitMethodToFlags(e.target.value)
    setServiceTypes(
      serviceTypes.map((t) =>
        t.VisitTypeId === id ?
          {
            ...t,
            IsPhoneVisitCommunicationTypeAllowed: flags.IsPhoneVisitCommunicationTypeAllowed,
            IsInPerson: flags.IsInPerson,
            IsAsynchronous: flags.IsAsynchronous,
          }
        : t,
      ),
    )
  }

  const onDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) =>
    updateWithErrors('Description', e.target.value, {
      visitDescriptionError: false,
      visitDescriptionLocalError: '',
    })

  const onCostChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (!onlyNumbersRegex.test(val) || val === '.') return
    updateWithErrors('Cost', val, { coastUSDError: false, coastUSDLocalError: '' })
  }

  const onCancellationFeeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (!onlyNumbersRegex.test(val) || val === '.') return
    updateWithErrors('CancellationFee', val, {
      cancellationFeeError: false,
      cancellationFeeLocalError: '',
    })
  }

  const onVisibilityChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setServiceTypes(
      serviceTypes.map((t) =>
        t.VisitTypeId === id ?
          { ...t, VisibilityType: e.target.value, errors: { ...t.errors, visibilityTypeError: '' } }
        : { ...t, errors: { ...t.errors, visibilityTypeError: '' } },
      ),
    )
  }

  const onIntakeFormChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedName = e.target.value
    const selectedForm = intakeForms?.find((f) => f.Name === selectedName)
    setServiceTypes(
      serviceTypes.map((t) =>
        t.VisitTypeId === id ?
          {
            ...t,
            IntakeFormId: selectedForm?.IntakeFormId ?? '',
            errors: { ...t.errors, intakeFormError: false, intakeFormLocalError: '' },
          }
        : t,
      ),
    )
  }

  const onConsentFormsChange = useCallback(
    (selectedIds: string[]) => {
      // selectedConsentIds is derived from serviceTypeConfig.ConsentForms, so we only need
      // to update serviceTypes — the derived value will follow on the next render.
      setServiceTypes(
        serviceTypes.map((t) =>
          t.VisitTypeId === id ?
            {
              ...t,
              // Carry the runtime `id` + `isActive` the validation/save paths require, alongside
              // ConsentFormId/Name — otherwise a selected consent fails validation and never saves.
              ConsentForms: selectedIds.map((cid) => ({
                id: cid,
                ConsentFormId: cid,
                isActive: true,
                Name: consentOptions.find((o) => o.value === cid)?.label ?? null,
              })),
              errors: { ...t.errors, consentFormError: false, consentFormLocalError: '' },
            }
          : t,
        ),
      )
    },
    [id, serviceTypes, setServiceTypes, consentOptions],
  )

  const intakeFormValue = (() => {
    if (IntakeFormId) return intakeForms?.find((f) => f.IntakeFormId === IntakeFormId)?.Name ?? ''
    return IntakeForm?.Name ?? ''
  })()

  return (
    /*
     * SCSS → Tailwind mapping notes:
     * .vtf-panel: bg-white p-sp-3 flex flex-col gap-sp-2
     * .vtf-row-3: grid [grid-template-columns:2fr_1fr_1fr] gap-sp-2 items-start [&>*]:min-w-0
     * .vtf-row-2: grid grid-cols-2 gap-sp-2 items-start [&>*]:min-w-0
     * .vtf-row-3-unequal: same as vtf-row-3
     * .vtf-field: flex flex-col gap-sp-0.5
     * .vtf-label: block mb-sp-0.5
     * .vtf-field-footer: flex items-center justify-between min-h-[16px]
     * .vtf-error: text-text-error  (DS text.error = red[70] = #703622; SCSS #d94444 ≈ no exact DS token —
     *             using text-text-error which maps to DS red-70; closest DS token to #d94444 is red-50=#CB4D25
     *             but red-70 is the canonical error token used in the DS itself)
     * .vtf-char-count: text-size-2 font-normal leading-4 text-text-light ml-auto
     * .vtf-multiselect > button: py-[10px]  (arbitrary — override inner DS MultiSelect trigger height)
     * .vtf-toggle: flex flex-col gap-sp-0.5
     * .vtf-toggle-row: flex items-center gap-sp-1 pt-sp-0.5
     */
    <>
      {/* Panel 1: Visit info + description */}
      <div className='flex flex-col gap-sp-2 bg-white p-sp-3'>
        <div className='grid items-start gap-sp-2 [grid-template-columns:2fr_1fr_1fr] [&>*]:min-w-0'>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Visit name
            </Label>
            <Input
              value={Name ?? ''}
              onChange={onNameChange}
              placeholder='Enter visit name'
              error={!!errors.visitNameError}
            />
            {errors.visitNameLocalError ?
              <Label size='sm' className='text-text-error'>
                {errors.visitNameLocalError}
              </Label>
            : null}
          </div>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Visit length
            </Label>
            <Select value={Duration} onChange={onDurationChange} error={!!errors.visitLengthError}>
              {DURATION_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
            {errors.visitLengthLocalError ?
              <Label size='sm' className='text-text-error'>
                {errors.visitLengthLocalError}
              </Label>
            : null}
          </div>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Visit method
            </Label>
            <Select value={visitMethod} onChange={onVisitMethodChange}>
              <option value={VISIT_METHOD_VIDEO_PHONE}>Video &amp; Phone call</option>
              <option value={VISIT_METHOD_VIDEO_ONLY}>Video only</option>
              <option value={VISIT_METHOD_IN_PERSON}>In-person visit</option>
              <option value={VISIT_METHOD_OFFLINE_ASYNC}>Offline (async)</option>
            </Select>
          </div>
        </div>
        <div className='flex flex-col gap-sp-0.5'>
          <Label size='md' className='mb-sp-0.5 block'>
            Description
          </Label>
          <Textarea
            value={Description ?? ''}
            onChange={onDescriptionChange}
            placeholder='Type your message...'
            rows={4}
            error={!!errors.visitDescriptionError}
          />
          <div className='flex min-h-[16px] items-center justify-between'>
            {errors.visitDescriptionLocalError ?
              <Label size='sm' className='text-text-error'>
                {errors.visitDescriptionLocalError}
              </Label>
            : <span />}
            <span className='ml-auto text-size-2 font-normal leading-4 text-text-light'>
              {(Description ?? '').length} / {VISIT_DESCRIPTION_MAX}
            </span>
          </div>
        </div>
      </div>

      {/* Panel 2: Cost + Cancellation fee */}
      <div className='flex flex-col gap-sp-2 bg-white p-sp-3'>
        <div className='grid grid-cols-2 items-start gap-sp-2 [&>*]:min-w-0'>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Cost (USD)
            </Label>
            <Input value={Cost} onChange={onCostChange} placeholder='Cost (USD)' error={!!errors.coastUSDError} />
            {errors.coastUSDLocalError ?
              <Label size='sm' className='text-text-error'>
                {errors.coastUSDLocalError}
              </Label>
            : null}
          </div>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Cancellation fee (USD)
            </Label>
            <Input
              value={CancellationFee}
              onChange={onCancellationFeeChange}
              placeholder='Cancellation fee (USD)'
              error={!!errors.cancellationFeeError}
            />
            {errors.cancellationFeeLocalError ?
              <Label size='sm' className='text-text-error'>
                {errors.cancellationFeeLocalError}
              </Label>
            : null}
          </div>
        </div>
      </div>

      {/* Panel 3: Forms, visibility, toggles */}
      <div className='flex flex-col gap-sp-2 bg-white p-sp-3'>
        <div className='grid grid-cols-2 items-start gap-sp-2 [&>*]:min-w-0'>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Consent forms
            </Label>
            <MultiSelect
              value={selectedConsentIds}
              onChange={onConsentFormsChange}
              options={consentOptions}
              placeholder='Select consent form(s)'
              error={!!errors.consentFormError}
              className='[&>button]:py-[10px]'
            />
            {errors.consentFormLocalError ?
              <Label size='sm' className='text-text-error'>
                {errors.consentFormLocalError}
              </Label>
            : null}
          </div>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Intake form
            </Label>
            <Select value={intakeFormValue} onChange={onIntakeFormChange} error={!!errors.intakeFormError}>
              <option value='' disabled>
                Select intake form
              </option>
              {intakeForms?.map((f) => (
                <option key={f.IntakeFormId ?? ''} value={f.Name ?? ''}>
                  {f.Name}
                </option>
              ))}
            </Select>
            {errors.intakeFormLocalError ?
              <Label size='sm' className='text-text-error'>
                {errors.intakeFormLocalError}
              </Label>
            : null}
          </div>
        </div>
        <div className='grid items-start gap-sp-4 [grid-template-columns:2fr_1fr_1fr_1fr] [&>*]:min-w-0'>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Visible to
            </Label>
            <Select value={VisibilityType ?? ''} onChange={onVisibilityChange} error={!!errors.visibilityTypeError}>
              {VISIBILITY_OPTIONS.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            {errors.visibilityTypeError ?
              <Label size='sm' className='text-text-error'>
                {errors.visibilityTypeError}
              </Label>
            : null}
          </div>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Drug screening
            </Label>
            <div className='flex items-center gap-sp-1 pt-sp-0.5'>
              <Switch
                checked={!!IsMedicationAssistedTreatment}
                onChange={(v) => update('IsMedicationAssistedTreatment', v)}
              />
              <Label size='md'>{IsMedicationAssistedTreatment ? 'Yes' : 'No'}</Label>
            </div>
          </div>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              Booking reminders
            </Label>
            <div className='flex items-center gap-sp-1 pt-sp-0.5'>
              <Switch
                checked={!!IsPostBookingReminderRequired}
                onChange={(v) => update('IsPostBookingReminderRequired', v)}
              />
              <Label size='md'>{IsPostBookingReminderRequired ? 'Yes' : 'No'}</Label>
            </div>
          </div>
          <div className='flex flex-col gap-sp-0.5'>
            <Label size='md' className='mb-sp-0.5 block'>
              New patient spacing
            </Label>
            <div className='flex items-center gap-sp-1 pt-sp-0.5'>
              <Switch
                checked={!!IsNewPatientSpacingEnabled}
                onChange={(v) => update('IsNewPatientSpacingEnabled', v)}
              />
              <Label size='md'>{IsNewPatientSpacingEnabled ? 'Yes' : 'No'}</Label>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
